import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { MomentModel } from '../../../entities';
import { RedactionMomentTypes } from '../../../entities/player';
import { useAnchor } from '../../../services/overlays';
import { TrainerTimelineState } from '../trainerTimelineState';
import classNames from 'classnames';
import { getMomentConfidenceThresholdColor } from '../../../entities/momentUtils';
import { parseRedaction } from '../../trainerRedaction/trainerRedactionUtils';

type Props = {
  moment: MomentModel,
  model: TrainerTimelineState
}

export const TimelineMoment = observer(({
  model,
  moment
}: Props) => {

  const isRedaction = Object.values(RedactionMomentTypes).includes(
    moment.momentType as typeof RedactionMomentTypes[keyof typeof RedactionMomentTypes]
  );
  const storedRedaction = parseRedaction(moment.description);
  const isVisualRedaction = storedRedaction?.kind === 'visual';
  const [draftTimes, setDraftTimes] = useState<{ startTime: number; endTime: number } | null>(null);
  const displayStart = draftTimes?.startTime ?? moment.startTime;
  const displayEnd = draftTimes?.endTime ?? moment.endTime;

  const hiddenTranscript = !!(moment.isTranscript && moment.minConfidencePercentage) && (moment.minConfidencePercentage > model.confidence);

  const color = moment.minConfidencePercentage && getMomentConfidenceThresholdColor(moment.minConfidencePercentage);

  const className = classNames("track-item", (moment.source || '').toLowerCase(), (moment.clipType || '').toLowerCase(), {
    'user-visible': moment.visibleToConsumer,
    'user-hidden': !moment.visibleToConsumer,
    'redaction-moment': isRedaction,
    'confidence-hidden': hiddenTranscript,
    'confidence-active': !hiddenTranscript && !!color
  });

  const style = {
    '--border-color': color,
    left: (displayStart / model.durationLayout) * 100 + '%',
    width: ((displayEnd - displayStart) / model.durationLayout) * 100 + '%'
  };

  const isDisabled = !model.sourcesFilter.has(moment.source) || hiddenTranscript;

  // TODO: defer anchor registration until the window is opened to avoid registering unused anchors
  const [anchorRef] = useAnchor({
    target: model,
    id: 'timeline.moment#' + moment.id
  });

  const startResize = (edge: 'start' | 'end') => (evt: React.PointerEvent<HTMLButtonElement>) => {
    const container = model.containerProxy.current;
    if (!container)
      return;

    evt.preventDefault();
    evt.stopPropagation();
    const initialX = evt.clientX;
    const initialStart = moment.startTime;
    const initialEnd = moment.endTime;
    const secondsPerPixel = model.durationLayout / container.getBoundingClientRect().width;
    let nextStart = initialStart;
    let nextEnd = initialEnd;

    const handleMove = (moveEvt: PointerEvent) => {
      const delta = (moveEvt.clientX - initialX) * secondsPerPixel;
      if (edge === 'start')
        nextStart = Math.max(0, Math.min(initialStart + delta, initialEnd - 0.1));
      else
        nextEnd = Math.min(model.duration || model.durationLayout, Math.max(initialEnd + delta, initialStart + 0.1));

      setDraftTimes({ startTime: nextStart, endTime: nextEnd });
    };

    const handleUp = () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      setDraftTimes(null);
      if (nextStart !== initialStart || nextEnd !== initialEnd)
        model.resizeRedactionMoment(moment, nextStart, nextEnd);
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
  };

  return (
    <div
      ref={anchorRef as React.RefCallback<HTMLDivElement>}
      className={className}
      style={style}
      title={isVisualRedaction ? 'Click to edit visual redaction' : undefined}
      aria-disabled={isDisabled}
      aria-selected={model.selectedMomentIds.includes(moment.id)}
      onClick={evt => {
        if (isVisualRedaction) {
          evt.stopPropagation();
          model.redaction.editVisualRedaction(moment);
          return;
        }

        model.handleTimelineMomentClick(evt, moment);
      }}>
      {isRedaction && (
        <>
          <button
            type="button"
            className="redaction-resize-handle start"
            aria-label="Adjust redaction start time"
            onClick={evt => evt.stopPropagation()}
            onPointerDown={startResize('start')} />
          <button
            type="button"
            className="redaction-resize-handle end"
            aria-label="Adjust redaction end time"
            onClick={evt => evt.stopPropagation()}
            onPointerDown={startResize('end')} />
        </>
      )}
    </div>
  );
});
